import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaDownload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../../css/header.css';
import whe2Logo from '../../images/logo.svg';

const headerCities = [
  'Johannesburg',
  'Cape Town',
  'Durban',
  'Pretoria',
  'Polokwane',
  'Mbombela',
  'Bloemfontein',
  'Kimberley',
  'George',
  'Mthatha',
  'Paarl',
  'Upington'
];

const Header = () => {
  const navigate = useNavigate();
  const headerContentRef = useRef(null);
  const cityStripRef = useRef(null);
  const citySequenceRef = useRef(null);
  const [headerContentWidth, setHeaderContentWidth] = useState(0);
  const [focusCenterX, setFocusCenterX] = useState(0);
  const [cityStripWidth, setCityStripWidth] = useState(0);
  const [citySequenceWidth, setCitySequenceWidth] = useState(0);
  const [cityOffset, setCityOffset] = useState(0);

  const repeatedCities = useMemo(() => [...headerCities], []);

  useEffect(() => {
    const measure = () => {
      const headerContentRect = headerContentRef.current?.getBoundingClientRect();
      const swapButtonRect = document.querySelector('.swap-button-container')?.getBoundingClientRect();

      setHeaderContentWidth(headerContentRef.current?.offsetWidth || 0);
      setCityStripWidth(cityStripRef.current?.offsetWidth || 0);
      setCitySequenceWidth(citySequenceRef.current?.scrollWidth || 0);
      setFocusCenterX(
        headerContentRect && swapButtonRect
          ? swapButtonRect.left + (swapButtonRect.width / 2) - headerContentRect.left
          : (headerContentRect?.width || 0) / 2
      );
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });
    const timeoutId = window.setTimeout(measure, 150);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (!cityStripWidth || !citySequenceWidth) {
      return undefined;
    }

    let frameId;
    let lastTimestamp;
    const speed = 10;

    const tick = (timestamp) => {
      if (lastTimestamp == null) {
        lastTimestamp = timestamp;
      }

      const delta = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      setCityOffset((previousOffset) => {
        const nextOffset = previousOffset + speed * delta;
        return nextOffset >= citySequenceWidth ? nextOffset - citySequenceWidth : nextOffset;
      });

      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [citySequenceWidth, cityStripWidth]);

  const handleDownloadClick = () => {
    navigate('/', { state: { scrollTarget: 'download' } });
  };

  const handleGuidesClick = () => {
    navigate('/guides');
  };

  const getCityStyle = (index, cycle = 0) => {
    if (!headerContentWidth || !cityStripWidth || !citySequenceWidth) {
      return undefined;
    }

    const positionInSequence = (index + 0.5) / headerCities.length;
    const baseCenter = positionInSequence * citySequenceWidth;
    const cycleOffset = cycle * citySequenceWidth;
    const cityStripLeftOffset = (headerContentWidth - cityStripWidth) / 2;
    const centerX = cityStripLeftOffset + baseCenter + cycleOffset - cityOffset;
    const focusX = focusCenterX || (headerContentWidth / 2);
    const normalizedDistance = Math.min(
      1,
      Math.abs(centerX - focusX) / (headerContentWidth / 2)
    );
    const emphasis = Math.pow(1 - normalizedDistance, 3.1);
    const scale = 0.52 + emphasis * 1.34;
    const opacity = 0.28 + emphasis * 0.72;
    const fontWeight = Math.round(380 + emphasis * 580);

    return {
      transform: `scale(${scale})`,
      opacity,
      fontWeight
    };
  };

  return (
    <header className="app-header">
      <div className="header-content" ref={headerContentRef}>
        <div className="header-logo-section">
          <div className="logo-container">
            <div className="logo-word-display">
              <div className="logo-display">
                <img
                  src={whe2Logo}
                  alt="whe2 Logo"
                  className="app-logo"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="header-city-strip" aria-hidden="true" ref={cityStripRef}>
          <div
            className="header-city-track"
            style={{ transform: `translateX(${-cityOffset}px)` }}
          >
            {[0, 1].map((cycle) => (
              <div
                className="header-city-sequence"
                key={cycle}
                ref={cycle === 0 ? citySequenceRef : undefined}
              >
                {repeatedCities.map((city, index) => (
                  <span className="header-city-pill" key={`${cycle}-${city}-${index}`} style={getCityStyle(index, cycle)}>
                    {city}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="header-actions">
          <button className="header-guides-link" type="button" onClick={handleGuidesClick}>
            <span>Guides</span>
          </button>
          <button className="header-download-link" type="button" onClick={handleDownloadClick}>
            <span>Download App</span>
            <FaDownload />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
